import { WorkSheetProductModelType } from '@teinor/erp/types/company/order/sell/workSheet/workSheetProduct'
import { useCallback, useMemo } from 'react'
import { RowData, TableData } from '../../../../../../../../../../baseComponents/TwinTable/types'
import { OrderData } from '../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/types'
import { parseSelectedProductsForProcessing } from '../../../../functions'
import { ButtonSecondary, ButtonDisabledOrLoader } from '../../../../../../../../../../baseComponents/Button'
import { ModalBigCentered } from '../../../../../../../../../../baseComponents/Modal'
import TwinTrans from '../../../../../../../../../../baseComponents/TwinTrans'
import { orderSortFunction } from '../../../../../../../../../../forms/CustomSelect/functions'
import FormRenderer from '../../../../../../../../../../forms/FormRenderer'
import { FormRender } from '../../../../../../../../../../forms/FormRenderer/types'
import TwinForm from '../../../../../../../../../../forms/TwinForm'
import { BeforeSubmitHandler, OnSubmit } from '../../../../../../../../../../forms/TwinForm/types'
import { SetEditDeleteModalTy, UseEditDeleteModalProps } from '../../../../../../../../../../utils/hooks/useEditDeleteModal'
import useTwinTranslation from '../../../../../../../../../../utils/hooks/useTwinTranslation'
import { getAllWorkSheetGroupStatuses } from '../../../../../../../../../../utils/reducers/getters'
import { DictWorkSheetProductStatusTy } from '../../../../../../../../../../utils/reducers/sells/workSheetGroupStatus'
import twinFetchPost from '../../../../../../../../../../utils/globals/data'
import { changeErrorMessage } from '../../../../../../../../../../utils/reducers/reduxDispatch'
import { faBallotCheck } from '@fortawesome/pro-light-svg-icons'
import { ProcessButton } from '../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderButtons'
import { TwinDictionary } from '../../../../../../../../../../utils/globals/dictionary'
import useIsLoading from '../../../../../../../../../../utils/hooks/useIsLoading'
import LoadingSpinner from '../../../../../../../../../../baseComponents/LoaderDecider/LoadingSpinner'
import './modalProcessWorkSheet.sass'

interface ModalProcessWorkSheetProductStatusProps { 
    getCompleteData: (inputtable?: TableData) => Promise<void>
    setOpenModal: SetEditDeleteModalTy
    completeData: OrderData
    allRowData: RowData
}

const ModalProcessWorkSheetProductStatus: React.FC<ModalProcessWorkSheetProductStatusProps> = ({ setOpenModal, completeData, getCompleteData }) => {
    const selectedProducts = useMemo(() => parseSelectedProductsForProcessing(completeData), [completeData])
    const workSheetProductStatuses = getAllWorkSheetGroupStatuses()
    return (
        <ModalBigCentered opened={true} setOpened={() => setOpenModal(null)} onClickOut={false} className={'flex modal_process_work_sheet '}>
            <ChangeWorkSheetProductStatus setOpenModal={setOpenModal} selectedProducts={selectedProducts} WorkSheetId={completeData?.header?.id} getCompleteData={getCompleteData} workSheetProductStatuses={workSheetProductStatuses?.dictWorkSheetGroupStatus?.[completeData?.header?.WorkSheetGroupStatusId]?.WorkSheetProductStatusesDict || {}}/>
                <div className='flex flex-col modal_process_rightside'>
                <FinishWorkSheetProducts {...{ setOpenModal, completeData, getCompleteData }} />
                <ButtonSecondary onClick={() => setOpenModal(null)} className='w-100 ml-auto mt-30 regular14'>
                    <TwinTrans transKey='back'>Volver</TwinTrans>
                </ButtonSecondary>
            </div>
        </ModalBigCentered>
    )
}

interface ChangeWorkSheetProductStatusProps extends ChangeWorkSheetProductStatusLogicProps {
    WorkSheetId: number
}

const ChangeWorkSheetProductStatus: React.FC<ChangeWorkSheetProductStatusProps> = ({ WorkSheetId, workSheetProductStatuses, ...rest }) => {
    const { t } = useTwinTranslation()
    const { handleBeforeSubmit, handleOnSubmit, loading } = useChangeWorkSheetProductStatusLogic({ workSheetProductStatuses, ...rest })
    const fields: FormRender<WorkSheetProductModelType> = [
        {
            cols: 1,
            elements: [
                {
                    name: 'WorkSheetProductStatusId',
                    component: 'CustomSelectWithSearchBar',
                    label: t('status', 'Estado'),
                    items: workSheetProductStatuses || {},
                    sortFunction: orderSortFunction
                },
                {
                    name: 'WorkSheetId',
                    component: 'InputHidden',
                    value: WorkSheetId
                },
            ]
        }
    ]
    return (
        <div className='bg-gray-F7 change_worksheet_product_status'>
            <h4 className='text-start mb-10'><TwinTrans transKey='changeStatusOrderProductsFromSelectedProducts'>Cambiar estado de los productos seleccionados</TwinTrans></h4>
            <TwinForm action='/api/app/order/sell/workSheet/workSheetProduct/changeWorkSheetProductStatus' onSubmit={handleOnSubmit} beforeSubmitHandler={handleBeforeSubmit} triggerErrors={false}>
                <FormRenderer sections={fields} />
                <ButtonDisabledOrLoader buttonIsDisabled={!workSheetProductStatuses || !Object.keys(workSheetProductStatuses)?.length} textButton={t('changeStatus', 'Cambiar de estado')} loading={loading} className='mt-40 w-full' />
            </TwinForm>
        </div>
    )
}

interface ChangeWorkSheetProductStatusLogicProps {
    selectedProducts: number[]
    workSheetProductStatuses: DictWorkSheetProductStatusTy
    setOpenModal: SetEditDeleteModalTy
    getCompleteData: (inputtable?: TableData) => Promise<void>
}

const useChangeWorkSheetProductStatusLogic = ({ selectedProducts, workSheetProductStatuses, setOpenModal, getCompleteData }: ChangeWorkSheetProductStatusLogicProps) => {
    const { startLoading, endLoading, loading } = useIsLoading()
    const handleBeforeSubmit: BeforeSubmitHandler = useCallback((values: any) => {
        startLoading?.()
        values['selectedProducts'] = selectedProducts
        values['typeStatus'] = workSheetProductStatuses?.[values.WorkSheetProductStatusId]?.typeStatus
        return true
    }, [selectedProducts, workSheetProductStatuses, startLoading])

    const handleOnSubmit: OnSubmit = useCallback(async (res: Response, values: any) => {
        let openModal: UseEditDeleteModalProps = null
        if (res.status === 422) {
            const responseJSON = await res.json()
            if (responseJSON.errors[0].msg?.includes('matchProduct')) {
                openModal = {
                    type: 'edit',
                    allRowData: { matchProducts: responseJSON.errors[0].msg } 
                }
            }
        }
        if (workSheetProductStatuses?.[values.WorkSheetProductStatusId]?.LabelId) { 
            let extraParamsForTag: TwinDictionary = { id: values.WorkSheetId, LabelId: workSheetProductStatuses?.[values.WorkSheetProductStatusId]?.LabelId, SpecificPriceRuleId: workSheetProductStatuses?.[values.WorkSheetProductStatusId]?.SpecificPriceRuleForLabelId, workSheetProductsIds: selectedProducts }
            if (openModal?.allRowData) {
                extraParamsForTag['extraParamsForMatch'] = openModal?.allRowData
            }
            openModal = { type: 'delete', allRowData: extraParamsForTag }
        }
        if (openModal) {
            setOpenModal(openModal)
        } else {
            setOpenModal(null)
            getCompleteData()
        }
        endLoading?.()
        return false
    }, [setOpenModal, selectedProducts, workSheetProductStatuses, getCompleteData, endLoading])

    return { handleBeforeSubmit, workSheetProductStatuses, handleOnSubmit, loading }
}


interface FinishWorkSheetProductsProps extends FinishWorkSheetProductsLogicProps { }

const FinishWorkSheetProducts: React.FC<FinishWorkSheetProductsProps> = ({ setOpenModal, completeData, getCompleteData }) => {
    const { t } = useTwinTranslation()
    const { finalizeOrder, loading } = useFinishWorkSheetProductsLogic({ setOpenModal, completeData, getCompleteData })
    return (
        <div className='finish_order'>
            <h4 className='text-start'><TwinTrans transKey='FinishWorkSheetProductSelectedProducts'>Marcar como finalizada la hoja de trabajo</TwinTrans></h4>
            <div className='flex flex-col gap-y-15 mt-20'>
                {loading ? <LoadingSpinner /> :
                    <div className='flex gap-20 flex-auto w-200 m-auto'>
                        <ProcessButton finalizeOrder={finalizeOrder} url='/api/app/order/sell/workSheet/finishWorkSheet' navigateTo={''} text={t('finish', 'Finalizar')} icon={faBallotCheck} />
                    </div>
                }
            </div>
        </div>
    )
}

interface FinishWorkSheetProductsLogicProps {
    setOpenModal: SetEditDeleteModalTy
    completeData: OrderData
    getCompleteData: (inputtable?: TableData) => Promise<void>
}

const useFinishWorkSheetProductsLogic = ({ setOpenModal, completeData, getCompleteData }: FinishWorkSheetProductsLogicProps) => {
    const { startLoading, endLoading, loading } = useIsLoading()
    const finalizeOrder = useCallback(async (url: string) => {
        startLoading?.()
        const response = await twinFetchPost(url, { id: completeData?.header?.id })
        const responseJSON = await response.json()
        if (response.status === 200) {
            await getCompleteData()
        } else {
            changeErrorMessage(responseJSON.errors[0].msg)
        }
        endLoading?.()
        setOpenModal(null)
    }, [setOpenModal, getCompleteData, completeData, startLoading, endLoading])

    return { finalizeOrder, loading }
}


export default ModalProcessWorkSheetProductStatus